import {
  ActivityStandard,
  FormatValues,
  LearningNeed,
} from "@outschool/business-rules";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useMemo } from "react";

import type { CategoryNavLink, Translate } from "./navbarCategories";

type SearchPathFilters = Record<string, string | number>;

const PRE_K_AND_KINDERGARTEN_GRADE_FILTER = "Pre-Kindergarten,Kindergarten";
const ACADEMIC_SUBJECT_FILTERS = {
  english: "English",
  math: "Math",
  science: "Science & Nature",
  socialStudies: "Social Studies",
} as const;
const GRADE_LINKS = [
  ["pre-k-and-kindergarten", PRE_K_AND_KINDERGARTEN_GRADE_FILTER],
  ["1st-grade", "1st grade"],
  ["2nd-grade", "2nd grade"],
  ["3rd-grade", "3rd grade"],
  ["4th-grade", "4th grade"],
  ["5th-grade", "5th grade"],
  ["6th-grade", "6th grade"],
  ["7th-grade", "7th grade"],
  ["8th-grade", "8th grade"],
  ["9th-grade", "9th grade"],
  ["10th-grade", "10th grade"],
  ["11th-grade", "11th grade"],
  ["12th-grade", "12th grade"],
] as const;
const LEARNING_NEED_FILTERS = [
  ["neurodiverse", { q: "neurodiverse" }],
  ["adhd", { learningNeeds: LearningNeed.ADHD }],
  ["autism", { learningNeeds: LearningNeed.ASD }],
  ["dyslexia", { learningNeeds: LearningNeed.DyslexiaDyscalculiaDysgraphia }],
  ["gifted", { q: "gifted" }],
] as const;

function searchCategoryPath(filters: SearchPathFilters): string {
  return searchPath(filters);
}

function cloneWithSearchPaths(category: CategoryNavLink): CategoryNavLink {
  return {
    ...category,
    path:
      category.path ??
      searchCategoryPath({ q: category.slug.replace(/-/g, " ") }),
    getSubcategories: category.getSubcategories
      ? () => category.getSubcategories!().map(cloneWithSearchPaths)
      : undefined,
  };
}

function cloneSelfPacedWithSearchPaths(
  category: CategoryNavLink,
): CategoryNavLink {
  const isTopLevelSelfPaced = category.slug === "self-paced";
  const canLink = category.can_link !== false;
  return {
    ...category,
    path: canLink
      ? searchCategoryPath({
          format: FormatValues.SelfPaced,
          ...(isTopLevelSelfPaced
            ? {}
            : { q: category.slug.replace(/-/g, " ") }),
        })
      : category.path,
    getSubcategories: category.getSubcategories
      ? () => category.getSubcategories!().map(cloneSelfPacedWithSearchPaths)
      : undefined,
  };
}

function gradeNavLinks({
  t,
  slugSuffix,
  getFilters,
}: {
  t: Translate;
  slugSuffix: string;
  getFilters: (gradeLevel: string) => SearchPathFilters;
}): CategoryNavLink[] {
  const gradeLabels = [
    t("Pre-K & Kindergarten"),
    t("1st Grade"),
    t("2nd Grade"),
    t("3rd Grade"),
    t("4th Grade"),
    t("5th Grade"),
    t("6th Grade"),
    t("7th Grade"),
    t("8th Grade"),
    t("9th Grade"),
    t("10th Grade"),
    t("11th Grade"),
    t("12th Grade"),
  ];
  return GRADE_LINKS.map(([slug, gradeLevel], index) => ({
    nameTitleCased: gradeLabels[index],
    slug: `${slug}-${slugSuffix}`,
    path: searchCategoryPath(getFilters(gradeLevel)),
  }));
}

function learningNeedsNavLinks({
  t,
  extraFilters = {},
}: {
  t: Translate;
  extraFilters?: SearchPathFilters;
}): CategoryNavLink[] {
  const slugSuffix = extraFilters.capacityMax ? "-tutoring" : "";
  const labels = [
    t("Neurodiverse"),
    t("ADHD"),
    t("Autism"),
    t("Dyslexia"),
    t("Gifted"),
  ];
  return LEARNING_NEED_FILTERS.map(([slug, filters], index) => ({
    nameTitleCased: labels[index],
    slug: `${slug}${slugSuffix}`,
    path: searchCategoryPath({ ...extraFilters, ...filters }),
  }));
}

export function useBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
  selfPacedCategory,
}: {
  electivesCategory: CategoryNavLink;
  justForFunCategory: CategoryNavLink;
  selfPacedCategory: CategoryNavLink;
}): CategoryNavLink[] {
  const { t } = useTranslation(
    "client\\components\\nav\\CategoryNavbar\\BackToSchoolNavbarCategories",
  );

  return useMemo(
    () =>
      buildBackToSchoolNavbarCategories({
        electivesCategory,
        justForFunCategory,
        selfPacedCategory,
        t,
      }),
    [electivesCategory, justForFunCategory, selfPacedCategory, t],
  );
}

function buildBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
  selfPacedCategory,
  t,
}: {
  electivesCategory: CategoryNavLink;
  justForFunCategory: CategoryNavLink;
  selfPacedCategory: CategoryNavLink;
  t: Translate;
}): CategoryNavLink[] {
  const tutoringFilters = {
    capacityMax: 1,
    format: FormatValues.Live,
  };
  const fullCourseFilters = {
    capacityMin: 2,
    delivery: "Course",
  };

  const academicCurriculumCategory: CategoryNavLink = {
    nameTitleCased: t("Academic Curriculum"),
    slug: "academic-curriculum",
    showMore: true,
    can_link: true,
    path: searchCategoryPath(fullCourseFilters),
    getSubcategories: () => [
      {
        nameTitleCased: t("Full Curriculum"),
        slug: "full-curriculum",
        path: searchCategoryPath(fullCourseFilters),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "full-curriculum",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("English"),
        slug: "english",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.english,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.english,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.math,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.math,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.science,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.science,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies",
        path: searchCategoryPath({
          ...fullCourseFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies",
            getFilters: gradeLevel => ({
              ...fullCourseFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Learning Methods"),
        slug: "learning-methods",
        path: searchCategoryPath({ q: "learning methods" }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Project-Based"),
            slug: "project-based",
            path: searchCategoryPath({ q: "project based" }),
          },
          {
            nameTitleCased: t("Montessori"),
            slug: "montessori",
            path: searchCategoryPath({ q: "montessori" }),
          },
          {
            nameTitleCased: t("Waldorf"),
            slug: "waldorf",
            path: searchCategoryPath({ q: "waldorf" }),
          },
          {
            nameTitleCased: t("Common Core (CCSS)"),
            slug: "common-core-ccss",
            path: searchCategoryPath({ standards: ActivityStandard.CCSS }),
          },
          {
            nameTitleCased: t("Next Gen Science (NGSS)"),
            slug: "next-gen-science-ngss",
            path: searchCategoryPath({ standards: ActivityStandard.NGSS }),
          },
        ],
      },
      {
        nameTitleCased: t("Learning Needs"),
        slug: "learning-needs",
        path: searchCategoryPath({ q: "learning needs" }),
        getSubcategories: () => learningNeedsNavLinks({ t }),
      },
    ],
  };

  const tutoringCategory: CategoryNavLink = {
    nameTitleCased: t("Tutoring"),
    slug: "tutoring",
    showMore: true,
    can_link: true,
    path: searchCategoryPath(tutoringFilters),
    getSubcategories: () => [
      {
        nameTitleCased: t("Full Curriculum"),
        slug: "full-curriculum-tutoring",
        path: searchCategoryPath(tutoringFilters),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "full-curriculum-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("English"),
        slug: "english-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.english,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.english,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.math,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.math,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.science,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.science,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies-tutoring",
        path: searchCategoryPath({
          ...tutoringFilters,
          theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
        }),
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies-tutoring",
            getFilters: gradeLevel => ({
              ...tutoringFilters,
              theme: ACADEMIC_SUBJECT_FILTERS.socialStudies,
              gradeLevel,
            }),
          }),
      },
      {
        nameTitleCased: t("Learning Needs"),
        slug: "learning-needs-tutoring",
        path: searchCategoryPath({ ...tutoringFilters, q: "learning needs" }),
        getSubcategories: () =>
          learningNeedsNavLinks({ t, extraFilters: tutoringFilters }),
      },
      {
        nameTitleCased: t("Other Subjects"),
        slug: "other-subjects-tutoring",
        path: searchCategoryPath({ ...tutoringFilters, q: "tutoring" }),
        getSubcategories: () => [
          {
            nameTitleCased: t("Art"),
            slug: "art-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: "Arts",
            }),
          },
          {
            nameTitleCased: t("Coding"),
            slug: "coding-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: "Coding & Tech",
            }),
          },
          {
            nameTitleCased: t("Music"),
            slug: "music-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              theme: "Music",
            }),
          },
          {
            nameTitleCased: t("Public Speaking"),
            slug: "public-speaking-tutoring",
            path: searchCategoryPath({
              ...tutoringFilters,
              q: "public speaking",
            }),
          },
        ],
      },
    ],
  };

  const electivesAndEnrichmentCategory = cloneWithSearchPaths({
    ...electivesCategory,
    nameTitleCased: t("Electives & Enrichment"),
    slug: "electives-and-enrichment",
    path: searchCategoryPath({ q: "electives enrichment" }),
  });

  const socialClubsCategory = cloneWithSearchPaths({
    ...justForFunCategory,
    nameTitleCased: t("Social Clubs"),
    slug: "social-clubs",
    path: searchCategoryPath({ q: "social clubs" }),
  });
  const selfPacedSearchCategory =
    cloneSelfPacedWithSearchPaths(selfPacedCategory);

  return [
    academicCurriculumCategory,
    tutoringCategory,
    electivesAndEnrichmentCategory,
    socialClubsCategory,
    selfPacedSearchCategory,
  ];
}
